import { Spinner } from '../components/feedback';
import { CreateSourceFields, type CreateMethod } from './CreateSourceFields';
import { CreateAdvancedFields } from './CreateAdvancedFields';
import { CreateConnectionFields } from './CreateConnectionFields';
import { clientModsApi } from '../api/client-mod-templates';
import { CreateResourceFields, presets } from './CreateResourceFields';
import { addonKindsOf, clientTemplateFits, modLoaderOf, velocitySupportOf } from '@mcpanel/core';
import type { ModpackPreview } from '@mcpanel/core';
import {
  defaultGameSettings,
  gameSettingsFromProperties,
  gameSettingsProperties,
  GameSettingsSchema,
  WorldGenerationSchema,
  type GameSettings,
  type WorldGeneration,
} from '@mcpanel/core';
import { ResourceSpecSchema } from '@mcpanel/core';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api/client';
import { api3 } from '../api/client-p3';
import type { Template } from '../api/types-p3';
import { usePoll } from '../hooks/usePoll';
import type { ChangeRequest, SoftwareSpec } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { RequestReview } from '../components/RequestReview';
import { moveStep, StepTransition } from '../components/motion';

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}$/;

export function CreateServer() {
  const { notify } = useApp();
  const navigate = useNavigate();
  const [gameSettings, setGameSettings] = useState<GameSettings>(defaultGameSettings);
  const [worldGeneration, setWorldGeneration] = useState<WorldGeneration>({
    worldType: 'normal',
    generateStructures: true,
  });
  const [modpack, setModpack] = useState<ModpackPreview>();
  const [method, setMethod] = useState<CreateMethod>('empty');
  const [name, setName] = useState('');
  const [software, setSoftware] = useState<SoftwareSpec>({ family: 'paper', mcVersion: '' });
  const [presetId, setPresetId] = useState<string>('medium');
  const [presetTouched, setPresetTouched] = useState(false);
  const [custom, setCustom] = useState({ cores: 4, memoryMb: 12288, heapMb: 9216, heapMinMb: 9216, dataDiskGb: 60 });
  const [weight, setWeight] = useState<'heavy' | 'light'>('heavy');
  const [preferredNode, setPreferredNode] = useState<string>();
  const [ha, setHa] = useState(true);
  const [proxied, setProxied] = useState(true);
  const [request, setRequest] = useState<ChangeRequest>();
  const [templateId, setTemplateId] = useState('');
  const [clientTemplateId, setClientTemplateId] = useState<string | null>();
  const clientTemplates = usePoll(() => clientModsApi.list(), 300000);
  const routing = usePoll(() => api3.routing(), 300000);
  const worldOptions = usePoll(() => api.worldGenerationOptions(), 300000);
  const templates = usePoll(() => api3.templates(), 300000);
  const [busy, setBusy] = useState(false);

  const preset = presets.find((p) => p.id === presetId);
  const resources = preset
    ? {
        cores: preset.cores,
        memoryMb: preset.memoryMb,
        heapMb: preset.heapMb,
        heapMinMb: preset.heapMb,
        dataDiskGb: preset.dataDiskGb,
      }
    : custom;
  const isModded = addonKindsOf(software.family).mods;
  const nameValid = NAME_PATTERN.test(name);
  const heapValid = ResourceSpecSchema.safeParse(resources).success;
  const underHub = proxied && Boolean(velocitySupportOf(software.family));
  const hub = routing.data?.hub;
  // hub 配下（自分が hub でない）と Mod 入りのサーバーには送らない。版に合わないものは既定に戻す
  const chosenClientTemplate =
    isModded || (underHub && hub !== undefined && hub !== name)
      ? undefined
      : clientTemplateId === null
        ? null
        : clientTemplates.data?.find(
            (template) => template.id === clientTemplateId && clientTemplateFits(template, software.mcVersion),
          )?.id;

  const applyTemplate = (template: Template | undefined) => {
    setModpack(undefined);
    setTemplateId(template?.id ?? '');
    setGameSettings(
      template
        ? gameSettingsFromProperties({
            ...template.properties,
            ...(template.gameSettings ? gameSettingsProperties(template.gameSettings) : {}),
          })
        : defaultGameSettings,
    );
    if (!template) return;
    setWeight(template.weight ?? 'heavy');
    setPreferredNode(template.preferredNode);
    setSoftware(template.software);
    setProxied(template.proxied);
    const preset = presets.find(
      (p) =>
        p.cores === template.resources.cores &&
        p.dataDiskGb === template.resources.dataDiskGb &&
        p.memoryMb === template.resources.memoryMb &&
        p.heapMb === template.resources.heapMb &&
        (template.resources.heapMinMb ?? template.resources.heapMb) === p.heapMb,
    );
    setPresetTouched(true);
    if (preset) setPresetId(preset.id);
    else {
      setPresetId('custom');
      setCustom({ ...template.resources, heapMinMb: template.resources.heapMinMb ?? template.resources.heapMb });
    }
  };

  const selectModpack = (pack: ModpackPreview) => {
    setModpack(pack);
    setSoftware(
      addonKindsOf(software.family).mods &&
        addonKindsOf(software.family).plugins &&
        modLoaderOf(software.family) === modLoaderOf(pack.software.family)
        ? {
            family: software.family,
            mcVersion: pack.software.mcVersion,
            loaderVersion: pack.software.loaderVersion,
          }
        : pack.software,
    );
    setTemplateId('');
    setProxied(false);
    setPresetTouched(true);
    setPresetId('custom');
    setCustom({
      cores: 4,
      memoryMb: pack.recommendedMemoryMb + 2048,
      heapMb: pack.recommendedMemoryMb,
      heapMinMb: pack.recommendedMemoryMb,
      dataDiskGb: 60,
    });
  };

  const changeSoftware = (next: SoftwareSpec) => {
    setModpack(undefined);
    setSoftware(next);
    const modded = addonKindsOf(next.family).mods;
    if (!presetTouched) setPresetId(modded ? 'large' : 'medium');
  };

  const choosePreset = (id: string) => {
    setPresetTouched(true);
    setPresetId(id);
  };

  const backToForm = async () => {
    // 自分で作った未承認の依頼は取り下げる（承認待ちに残さない）
    if (request?.status === 'pending') await api.rejectRequest(request.id).catch(() => undefined);
    moveStep('step-back', () => setRequest(undefined));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await api.createRequest('create_server', {
        gameSettings: GameSettingsSchema.parse(gameSettings),
        worldGeneration: WorldGenerationSchema.parse(worldGeneration),
        name,
        software,
        resources,
        ha,
        weight,
        preferredNode,
        proxied: proxied && Boolean(velocitySupportOf(software.family)),
        ...(templateId ? { templateId } : {}),
        ...(modpack ? { modpackId: modpack.id } : {}),
        ...(chosenClientTemplate !== undefined ? { clientTemplateId: chosenClientTemplate } : {}),
      });
      moveStep('step-forward', () => setRequest(created));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  if (request) {
    return (
      <StepTransition key="confirm">
        <div className="stack" style={{ maxWidth: 760 }}>
          <div className="page-header">
            <div>
              <h1>作成内容の確認</h1>
              <p>内容を確認して、ワンタイムコードで承認すると作成が始まります。</p>
            </div>
          </div>
          <div className="card card-body">
            <RequestReview
              request={request}
              compact
              onDecided={(result) => {
                if (result.status === 'rejected') moveStep('step-back', () => setRequest(undefined));
                else navigate('/jobs');
              }}
            />
          </div>
          <button className="btn ghost" style={{ alignSelf: 'flex-start' }} onClick={() => void backToForm()}>
            ← 入力に戻る
          </button>
        </div>
      </StepTransition>
    );
  }

  return (
    <StepTransition key="form">
      <form className="stack" style={{ maxWidth: 760 }} onSubmit={(event) => void submit(event)}>
        <Link to="/" className="small muted" style={{ textDecoration: 'none' }}>
          ← ダッシュボード
        </Link>
        <div className="page-header">
          <div>
            <h1 data-search-id="create-server">サーバーを作成</h1>
            <p>現世・ネザー・エンドをまとめた 1 つのサーバーを、専用の VM に作ります。</p>
          </div>
        </div>

        <section className="card card-body stack">
          <h2>作り方</h2>
          <div className="row">
            {(
              [
                ['empty', '空から'],
                ['template', 'テンプレートから'],
                ['modpack', 'Modpack から'],
              ] as const
            ).map(([id, label]) => (
              <label className="check" key={id}>
                <input
                  type="radio"
                  name="method"
                  checked={method === id}
                  onChange={() => {
                    setMethod(id);
                    setModpack(undefined);
                    applyTemplate(undefined);
                    setSoftware({ family: 'paper', mcVersion: '' });
                    setProxied(true);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        </section>
        <section className="card card-body stack">
          <h2>名前</h2>
          <div className="field">
            <label htmlFor="server-name">サーバー ID（英小文字・数字・ハイフン。接続先のホスト名にも使います）</label>
            <input
              id="server-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              placeholder="survival"
              aria-invalid={name !== '' && !nameValid}
            />
            {name !== '' && !nameValid && (
              <span className="small" style={{ color: 'var(--danger)' }}>
                2〜31 文字の英小文字・数字・ハイフンにしてください。
              </span>
            )}
          </div>
        </section>

        <CreateSourceFields
          method={method}
          templates={templates.data}
          templateId={templateId}
          applyTemplate={applyTemplate}
          modpack={modpack}
          setModpack={setModpack}
          selectModpack={selectModpack}
          software={software}
          setSoftware={setSoftware}
          changeSoftware={changeSoftware}
        />
        <CreateConnectionFields
          proxied={proxied}
          setProxied={setProxied}
          clientTemplateId={clientTemplateId}
          setClientTemplateId={setClientTemplateId}
          templates={clientTemplates.data}
          hub={hub}
          isModded={isModded}
          family={software.family}
          mcVersion={software.mcVersion}
          name={name}
        />
        <CreateResourceFields presetId={presetId} choosePreset={choosePreset} custom={custom} setCustom={setCustom} />
        {!heapValid && <div className="alert danger">リソースとヒープの値を確認してください。</div>}
        <CreateAdvancedFields
          gameSettings={gameSettings}
          setGameSettings={setGameSettings}
          worldGeneration={worldGeneration}
          setWorldGeneration={setWorldGeneration}
          candidates={worldOptions.data?.customWorldTypes}
          mods={templates.data?.find((t) => t.id === templateId)?.mods ?? []}
          resources={resources}
          heapKey={templateId + presetId}
          heapValid={heapValid}
          onHeapChange={(heap) => {
            setPresetTouched(true);
            setPresetId('custom');
            setCustom({ ...resources, ...heap });
          }}
          weight={weight}
          setWeight={setWeight}
          preferredNode={preferredNode}
          setPreferredNode={setPreferredNode}
          ha={ha}
          setHa={setHa}
        />

        <div className="row">
          <span className="spacer" />
          <button
            className="btn primary"
            disabled={
              busy ||
              !nameValid ||
              (method === 'template' && !templateId) ||
              (method === 'modpack' && !modpack) ||
              !software.mcVersion ||
              !heapValid ||
              !GameSettingsSchema.safeParse(gameSettings).success ||
              !WorldGenerationSchema.safeParse(worldGeneration).success
            }
          >
            {busy && <Spinner />}
            内容を確認
          </button>
        </div>
      </form>
    </StepTransition>
  );
}
